import React, { useRef, useEffect, useState } from "react";
import "./FullscreenWrapper.css";

interface FullscreenWrapperProps {
    hotkey?: string; // e.g., "ctrl+shift+f"
    children?: React.ReactNode;
    className?: string;
}

export const FullscreenWrapper: React.FC<FullscreenWrapperProps> = ({
    hotkey = "ctrl+shift+f",
    children,
    className,
}) => {
    const ref = useRef<HTMLDivElement>(null);
    const [isFullscreen, setIsFullscreen] = useState(false);

    const toggleFullscreen = () => {
        if (!document.fullscreenElement && ref.current) {
            ref.current.requestFullscreen().catch(console.error);
        } else {
            document.exitFullscreen().catch(console.error);
        }
    };

    // hotkey support
    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            const keys = hotkey.toLowerCase().split("+").map(k => k.trim());
            const ctrl = keys.includes("ctrl") ? e.ctrlKey : true;
            const shift = keys.includes("shift") ? e.shiftKey : true;
            const alt = keys.includes("alt") ? e.altKey : true;
            const key = keys.find(k => !["ctrl", "shift", "alt"].includes(k));
            if (ctrl && shift && alt && key && e.key.toLowerCase() === key) {
                e.preventDefault();
                toggleFullscreen();
            }
        };
        document.addEventListener("keydown", handler);
        return () => document.removeEventListener("keydown", handler);
    }, [hotkey]);

    // track fullscreen state
    useEffect(() => {
        const changeHandler = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };
        document.addEventListener("fullscreenchange", changeHandler);
        return () =>
            document.removeEventListener("fullscreenchange", changeHandler);
    }, []);

    return (
        <div
            ref={ref}
            className={`${className || ""} custom-fullscreen-wrapper ${isFullscreen ? "fullscreen-active" : ""
                }`}
            style={{
                width: "100%",
                height: "100%",
                position: isFullscreen ? "fixed" : "relative",
                top: isFullscreen ? 0 : undefined,
                left: isFullscreen ? 0 : undefined,
                backgroundColor: isFullscreen ? "#fff" : undefined,
                zIndex: isFullscreen ? 9999 : undefined,
            }}
        >
            {children}

            {/* Floating fullscreen button */}
            <button
                className="fullscreen-button"
                onClick={toggleFullscreen}
                title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#333"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className={isFullscreen ? "fullscreen-icon rotate" : "fullscreen-icon"}
                >
                    <polyline points="3 9 3 3 9 3" />
                    <polyline points="15 3 21 3 21 9" />
                    <polyline points="21 15 21 21 15 21" />
                    <polyline points="9 21 3 21 3 15" />
                </svg>
            </button>
        </div>
    );
};
